import * as vp from 'vopay';
import { FunctionComponent, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Prompt, useHistory } from 'react-router-dom';

import { usePendingData } from '../../../../providers/PendingData.provider';
import { AddSignerDialogueHandler, DialogueHandlerProps } from './types';

import useStorage from '../../../../hooks/useStorage';
import usePostAddSigner from '../../../../hooks/useApi/usePostAddSigner';
import AddSignerForm from './AddSignerForm';
import AddSignerContext, { AddSignerCtx } from './AddSignerContext';
import AddSignerDialogue from './AddSignerDialogue';

const { Box, Title } = vp;

type FC = FunctionComponent;

const AddSignercontainer: FC = () => {
  const { t } = useTranslation('translations', { keyPrefix: 'AddSignerContainer' });
  const history = useHistory();
  const { hasPendingData, initPendingData, reset } = usePendingData();
  const {
    accountToken: { getAccountToken },
  } = useStorage();
  const accountToken = getAccountToken();
  const [addSigner, postAddSigner] = usePostAddSigner({ bearer: accountToken || '' });
  const {
    signerCreation: { storeSignerCreation },
  } = useStorage();

  const [shouldAddSigner, setShouldAddSigner] = useState(false);
  const [showAddSignerFailError, setShowAddSignerFailError] = useState(false);

  const AddSigner: AddSignerCtx = {
    showAddSignerFailError,
  };

  const defaultValues = {
    AuthorizedFullLegalName: '',
    AuthorizedEmailAddress: '',
    AuthorizedOccupation: '',
    AuthorizedDOB: '',
  };

  const handlers = {
    handleDialogue: (handlerProps: DialogueHandlerProps<AddSignerDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: AddSignerDialogueHandler = {
        error: {
          back: {
            default: () => {},
          },
          confirm: {
            default: () => {
              setShowAddSignerFailError(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
    onAddSigner: (value: vp.AddSignerRequest) => {
      postAddSigner(value);
      reset();
      setShouldAddSigner(true);
    },
  };

  useEffect(() => {
    if (shouldAddSigner) {
      setShouldAddSigner(false);

      if (addSigner.Success === false) {
        setTimeout(() => {
          setShowAddSignerFailError(true);
        }, 1500);
      }
    }
  }, [shouldAddSigner, addSigner.Success]);

  useEffect(() => {
    if (addSigner.Success) {
      history.goBack();
      storeSignerCreation('signerToast');
    }
  }, [addSigner.Success]);

  useEffect(() => {
    initPendingData(defaultValues);
  }, []);

  return (
    <AddSignerContext.Provider value={AddSigner}>
      <Box mt="lg" mb="lg" flx="col">
        <Title title={t('title', { defaultValue: 'Add Signer' })} />
      </Box>
      <AddSignerForm handlers={handlers} defaultValues={defaultValues} />

      <Prompt
        when={hasPendingData}
        message={t('unsavedPrompt', { defaultValue: 'You have unsaved signer data that will be discarded.' })}
      />

      <AddSignerDialogue handleDialogue={handlers.handleDialogue} />
    </AddSignerContext.Provider>
  );
};

export default AddSignercontainer;
